const inputClasses = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all disabled:bg-slate-50 disabled:text-slate-500";

function fieldLabel(label, isFrench) {
    if (label && typeof label === "object") {
        return isFrench ? (label.fr || label.en) : (label.en || label.fr);
    }
    return label;
}

function normalizeOption(opt, isFrench) {
    if (opt && typeof opt === "object") {
        return { value: opt.value, label: fieldLabel(opt.label, isFrench) };
    }
    return { value: opt, label: opt };
}

function renderRichLabel(label) {
    if (typeof label !== "string" || !label.includes("**")) return label;
    return label.split(/\*\*(.*?)\*\*/g).map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
}

function TableFieldInput({ field, value, onChange, readOnly, isFrench }) {
    const columns = field.columns || [];
    const minRows = field.minRows || 0;
    const savedRows = Array.isArray(value) ? value : [];

    const emptyRow = () => columns.reduce((acc, col) => ({ ...acc, [col.key]: "" }), {});
    const rows = savedRows.length > 0 ? savedRows : (readOnly ? [] : [emptyRow()]);

    const commit = (newRows) => {
        if (!readOnly && onChange) onChange(newRows);
    };

    const handleCellChange = (rowIndex, colKey, newValue) => {
        commit(rows.map((row, i) => (i === rowIndex ? { ...row, [colKey]: newValue } : row)));
    };

    const handleAddRow = () => commit([...rows, emptyRow()]);
    const handleRemoveRow = (rowIndex) => commit(rows.filter((_, i) => i !== rowIndex));

    if (rows.length === 0) {
        return <p className="text-sm text-slate-400 italic">{isFrench ? "Aucune entrée." : "No entries."}</p>;
    }

    return (
        <div className="space-y-3">
            <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-sm border-collapse min-w-[640px]">
                    <thead>
                        <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {columns.map((col) => (
                                <th key={col.key} className="px-3 py-2 border-b border-slate-200">
                                    {fieldLabel(col.label, isFrench)}
                                </th>
                            ))}
                            {!readOnly && <th className="px-3 py-2 border-b border-slate-200" />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b border-slate-100 last:border-0">
                                {columns.map((col) => (
                                    <td key={col.key} className="px-3 py-2 align-top">
                                        {col.type === "select" ? (
                                            <select
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm disabled:bg-slate-50"
                                                value={row[col.key] || ""}
                                                disabled={readOnly}
                                                onChange={(e) => handleCellChange(rowIndex, col.key, e.target.value)}
                                            >
                                                <option value="" disabled>{isFrench ? "Sélectionner…" : "Select…"}</option>
                                                {(col.options || []).map((opt) => {
                                                    const normalized = normalizeOption(opt, isFrench);
                                                    return <option key={normalized.value} value={normalized.value}>{normalized.label}</option>;
                                                })}
                                            </select>
                                        ) : col.type === "date" ? (
                                            <input
                                                type="date"
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm disabled:bg-slate-50"
                                                value={row[col.key] || ""}
                                                disabled={readOnly}
                                                onChange={(e) => handleCellChange(rowIndex, col.key, e.target.value)}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm disabled:bg-slate-50"
                                                value={row[col.key] || ""}
                                                disabled={readOnly}
                                                onChange={(e) => handleCellChange(rowIndex, col.key, e.target.value)}
                                            />
                                        )}
                                    </td>
                                ))}
                                {!readOnly && (
                                    <td className="px-3 py-2 align-top">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRow(rowIndex)}
                                            disabled={rows.length <= minRows}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 disabled:text-slate-300 disabled:cursor-not-allowed"
                                        >
                                            {isFrench ? "Retirer" : "Remove"}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!readOnly && (
                <button
                    type="button"
                    onClick={handleAddRow}
                    className="text-sm font-semibold text-sky-600 hover:text-sky-700"
                >
                    + {isFrench ? "Ajouter une ligne" : "Add a row"}
                </button>
            )}
        </div>
    );
}

export default function FormFieldRenderer({ field, value, onChange, readOnly, isFrench }) {
    const label = fieldLabel(field.label, isFrench);

    if (field.fieldType === "heading") {
        return (
            <div className="pt-4 first:pt-0">
                <h3 className="text-sm font-black uppercase tracking-wider text-sky-600 border-b border-slate-200 pb-2">
                    {label}
                </h3>
            </div>
        );
    }

    if (field.fieldType === "notice") {
        return (
            <div className="pt-2 first:pt-0">
                <p className="text-xl md:text-2xl font-black uppercase tracking-wide text-red-600">
                    {label}
                </p>
            </div>
        );
    }

    if (field.fieldType === "paragraph") {
        return (
            <p className="text-sm text-slate-500 leading-relaxed">
                {label}
            </p>
        );
    }

    if (field.fieldType === "personBadge") {
        const isSpouse = field.person === "spouse";
        return (
            <div className="pt-4 first:pt-0">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-black uppercase tracking-wider ${
                    isSpouse ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-sky-100 text-sky-700'
                }`}>
                    {label}
                </span>
            </div>
        );
    }

    const handleChange = (newValue) => {
        if (!readOnly && onChange) {
            onChange(field.fieldKey, newValue);
        }
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">
                {renderRichLabel(label)}
                {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {field.fieldType === "table" && (
                <TableFieldInput
                    field={field}
                    value={value}
                    onChange={handleChange}
                    readOnly={readOnly}
                    isFrench={isFrench}
                />
            )}

            {field.fieldType === "textarea" && (
                <textarea
                    className={inputClasses}
                    rows={4}
                    value={value || ""}
                    disabled={readOnly}
                    onChange={(e) => handleChange(e.target.value)}
                />
            )}

            {field.fieldType === "select" && (
                <select
                    className={inputClasses}
                    value={value || ""}
                    disabled={readOnly}
                    onChange={(e) => handleChange(e.target.value)}
                >
                    <option value="" disabled>
                        {isFrench ? "Sélectionner…" : "Select…"}
                    </option>
                    {(field.options || []).map((opt) => {
                        const normalized = normalizeOption(opt, isFrench);
                        return (
                            <option key={normalized.value} value={normalized.value}>{normalized.label}</option>
                        );
                    })}
                </select>
            )}

            {field.fieldType === "date" && (
                <input
                    type="date"
                    className={inputClasses}
                    value={value || ""}
                    disabled={readOnly}
                    onChange={(e) => handleChange(e.target.value)}
                />
            )}

            {field.fieldType === "checkbox" && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                        checked={!!value}
                        disabled={readOnly}
                        onChange={(e) => handleChange(e.target.checked)}
                    />
                    {isFrench ? "Oui" : "Yes"}
                </label>
            )}

            {field.fieldType === "yesno" && (
                <div className="flex gap-3">
                    <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => handleChange(true)}
                        className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed ${
                            value === true
                                ? 'border-sky-500 bg-sky-50 text-sky-700 ring-2 ring-sky-400'
                                : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                        {isFrench ? "Oui" : "Yes"}
                    </button>
                    <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => handleChange(false)}
                        className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed ${
                            value === false
                                ? 'border-sky-500 bg-sky-50 text-sky-700 ring-2 ring-sky-400'
                                : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                        {isFrench ? "Non" : "No"}
                    </button>
                </div>
            )}

            {(!field.fieldType || field.fieldType === "text") && (
                <input
                    type="text"
                    className={inputClasses}
                    value={value || ""}
                    disabled={readOnly}
                    onChange={(e) => handleChange(e.target.value)}
                />
            )}
        </div>
    );
}
